// LAP-SZINTŰ FIGYELMEZTETÉSEK — a szűrő-sor alatt álló EGYETLEN, lenyitható blokk, tónusonként csoportosítva.
//
// ══ MIÉRT NEM EGYSZERŰEN EGYMÁS ALÁ TETT SÁVOK ══════════════════════════════════════════════════════════
// A Pénzügy Összegzés fülén HAT jelzés futhat egyszerre (két árfolyam-minőség, csonkolt olvasás, hiányzó
// használati adat, kihagyás-napló, deviza-kiadás, NAV-hiány, besorolatlan tételek). Hat külön keretes sáv
// egymás alatt EGY TELJES KÉPERNYŐT elvett — a lap fő számai a hajtás alá kerültek, vagyis a figyelmeztetés
// pont attól tolta el a felhasználót, amit minősíteni akart (mérve 1920×1080-on).
//
// Tónusonként EGY keretbe fogva a blokk a töredékére zsugorodik, a sorok pedig behúzott hajszálvonallal
// válnak el — ugyanaz a vonal-nyelv, mint a lista-tábláinkban és a `PanelCard`-ban.
//
// ══ A SORREND = A SÚLY ══════════════════════════════════════════════════════════════════════════════════
// `danger` → `warning` → `neutral`. A hívó a saját listáján belüli sorrendet megtartja; a tónus-csoportok
// sorrendje viszont KÖTÖTT, hogy a lap tetején sose egy mellékes tudnivaló álljon egy alulbecslés előtt.
//
// ══ ÖSSZECSUKVA, A SZŰRŐ-SOR ALATT ══════════════════════════════════════════════════════════════════════
// A tónusonkénti keret is lejjebb tolta a lap tetejét: a jelzések a fül-sáv és a dátum/kereső sor KÖZÉ
// ékelődtek, így a lap minden nyitáskor a jelzésekkel kezdődött (user-lelet). Ezért a blokk egy lenyitható
// „Információk" sor a szűrő-sor alatt, ALAPBÓL ÖSSZECSUKVA. A zárt sor sem néma: tónusonként darabszámot és
// színes pöttyöt mutat („1 figyelmeztetés · 2 megjegyzés"), tehát a súly lenyitás nélkül is látszik — egy
// alulbecslés így nem tűnik el, csak nem foglal helyet. Natív `<details>`: billentyűzettel és
// képernyőolvasóval is kezelhető, állapot nélkül (a `UsageMissingList` precedense).
import {
  ChevronDownIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

/** Egy lap-szintű jelzés. A `tone` a SÚLY, a `title` a mondat (lehet link is). */
export interface PageNotice {
  key: string;
  tone: 'danger' | 'warning' | 'neutral';
  title: ReactNode;
}

const TONE_ORDER = ['danger', 'warning', 'neutral'] as const;

const TONE_BOX: Record<PageNotice['tone'], string> = {
  danger: 'border-danger/40 bg-danger-soft text-danger',
  warning: 'border-warning/40 bg-warning-soft text-warning',
  neutral: 'border-border/60 bg-sunken/40 text-muted-foreground',
};

/** A csoport-keret belső elválasztó vonala — a tónus saját színéből, halványan (nem szürke idegen vonal). */
const TONE_DIVIDE: Record<PageNotice['tone'], string> = {
  danger: 'divide-danger/20',
  warning: 'divide-warning/20',
  neutral: 'divide-border/60',
};

/** A zárt sor tónus-pöttye — a jelzés súlya lenyitás nélkül is látszik. */
const TONE_DOT: Record<PageNotice['tone'], string> = {
  danger: 'bg-danger',
  warning: 'bg-warning',
  neutral: 'bg-muted-foreground/50',
};

const TONE_ICON: Record<PageNotice['tone'], JSX.Element> = {
  danger: <ExclamationTriangleIcon className="size-4" />,
  warning: <ExclamationTriangleIcon className="size-4" />,
  neutral: <InformationCircleIcon className="size-4" />,
};

export function PageNotices({
  notices,
  className,
}: {
  notices: readonly PageNotice[];
  className?: string;
}): JSX.Element | null {
  const { t } = useTranslation();
  if (notices.length === 0) return null;
  const groups = TONE_ORDER.map((tone) => ({
    tone,
    items: notices.filter((n) => n.tone === tone),
  })).filter((g) => g.items.length > 0);
  return (
    <details className={cn('group', className)}>
      <summary className="text-muted-foreground hover:text-foreground flex cursor-pointer list-none items-center gap-2 rounded-md py-1 text-xs [&::-webkit-details-marker]:hidden">
        <InformationCircleIcon className="size-4 shrink-0" />
        <span className="text-foreground font-medium">{t('common.pageNotices.title')}</span>
        <span className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
          {groups.map((g) => (
            <span key={g.tone} className="inline-flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className={cn('size-1.5 shrink-0 rounded-full', TONE_DOT[g.tone])}
              />
              {t(`common.pageNotices.count.${g.tone}`, { count: g.items.length })}
            </span>
          ))}
        </span>
        <ChevronDownIcon className="ml-auto size-4 shrink-0 transition-transform group-open:rotate-180" />
      </summary>
      <div className="space-y-2 pt-2">
        {groups.map((g) => (
          <div
            key={g.tone}
            role="status"
            className={cn(
              'divide-y rounded-lg border text-xs',
              TONE_BOX[g.tone],
              TONE_DIVIDE[g.tone],
            )}
          >
            {g.items.map((n) => (
              <div key={n.key} className="flex items-center gap-3 px-3 py-2">
                <span className="shrink-0">{TONE_ICON[g.tone]}</span>
                <span className="min-w-0">{n.title}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </details>
  );
}
